/// <amd-module name="DistributedTask/Client/RestClient/Elastic" />
/**
 * ---------------------------------------------------------
 * Generated file, DO NOT EDIT
 * ---------------------------------------------------------
 *
 * See following wiki page for instructions on how to regenerate:
 *   https://aka.ms/azure-devops-client-generation
 *
 * Configuration file:
 *   distributedtask\client\webapi\clientgeneratorconfigs\genclient.json
 */

import { IVssPageContext, IVssRestClient, IVssRestClientOptions, RestClients } from "VSS/Platform/Context";
import { RestClientBase } from "VSS/Platform/RestClientBase";

import * as TaskAgent from "DistributedTask/Client/Contracts/TaskAgent";

export interface IElasticRestClient extends IVssRestClient {
    /**
     * Get elastic pool diagnostics logs for a specified Elastic Pool.
     * 
     * @param poolId - Pool Id of the Elastic Pool
     * @param top - Number of elastic pool logs to retrieve
     */
    getElasticPoolLogs(poolId: number, top?: number): Promise<TaskAgent.ElasticPoolLog[]>;
    /**
     * Create a new elastic pool. This will create a new TaskAgentPool at the organization level. If a project id is provided, this will create a new TaskAgentQueue in the specified project.
     * 
     * @param elasticPool - Elastic pool to create. Contains the properties necessary for configuring a new ElasticPool.
     * @param poolName - Name to use for the new TaskAgentPool
     * @param authorizeAllPipelines - Setting to determine if all pipelines are authorized to use this TaskAgentPool by default.
     * @param autoProvisionProjectPools - Setting to automatically provision TaskAgentQueues in every project for the new pool.
     * @param projectId - Optional: If provided, a new TaskAgentQueue will be created in the specified project.
     */
    createElasticPool(elasticPool: TaskAgent.ElasticPool, poolName: string, authorizeAllPipelines?: boolean, autoProvisionProjectPools?: boolean, projectId?: string): Promise<TaskAgent.ElasticPoolCreationResult>;
    /**
     * Returns the Elastic Pool with the specified Pool Id.
     * 
     * @param poolId - Pool Id of the associated TaskAgentPool
     */
    getElasticPool(poolId: number): Promise<TaskAgent.ElasticPool>;
    /**
     * Get a list of all Elastic Pools.
     * 
     */
    getElasticPools(): Promise<TaskAgent.ElasticPool[]>;
    /**
     * Update settings on a specified Elastic Pool.
     * 
     * @param elasticPoolSettings - New Elastic Pool settings data
     * @param poolId - 
     */
    updateElasticPool(elasticPoolSettings: TaskAgent.ElasticPoolSettings, poolId: number): Promise<TaskAgent.ElasticPool>;
    /**
     * Get a list of ElasticNodes currently in the ElasticPool
     * 
     * @param poolId - Pool id of the ElasticPool
     * @param state - Optional: Filter to only retrieve ElasticNodes in the given ElasticNodeState
     */
    getElasticNodes(poolId: number, state?: TaskAgent.ElasticNodeState): Promise<TaskAgent.ElasticNode[]>;
    /**
     * Update properties on a specified ElasticNode
     * 
     * @param elasticNodeSettings - 
     * @param poolId - 
     * @param elasticNodeId - 
     */
    updateElasticNode(elasticNodeSettings: TaskAgent.ElasticNodeSettings, poolId: number, elasticNodeId: number): Promise<TaskAgent.ElasticNode>;
}

class ElasticRestClient extends RestClientBase implements IElasticRestClient {
    constructor(options: IVssRestClientOptions) {
        super(options);
    }

    /**
     * Get elastic pool diagnostics logs for a specified Elastic Pool.
     * 
     * @param poolId - Pool Id of the Elastic Pool
     * @param top - Number of elastic pool logs to retrieve
     */
    public async getElasticPoolLogs(
        poolId: number,
        top?: number
        ): Promise<TaskAgent.ElasticPoolLog[]> {

        const queryValues: any = {
            '$top': top
        };

        return this.beginRequest<TaskAgent.ElasticPoolLog[]>({
            apiVersion: "6.1-preview.1",
            routeTemplate: "_apis/distributedtask/elasticpools/{poolId}/logs",
            routeValues: {
                poolId: poolId
            },
            queryParams: queryValues
        });
    }

    /**
     * Create a new elastic pool. This will create a new TaskAgentPool at the organization level. If a project id is provided, this will create a new TaskAgentQueue in the specified project.
     * 
     * @param elasticPool - Elastic pool to create. Contains the properties necessary for configuring a new ElasticPool.
     * @param poolName - Name to use for the new TaskAgentPool
     * @param authorizeAllPipelines - Setting to determine if all pipelines are authorized to use this TaskAgentPool by default.
     * @param autoProvisionProjectPools - Setting to automatically provision TaskAgentQueues in every project for the new pool.
     * @param projectId - Optional: If provided, a new TaskAgentQueue will be created in the specified project.
     */
    public async createElasticPool(
        elasticPool: TaskAgent.ElasticPool,
        poolName: string,
        authorizeAllPipelines?: boolean,
        autoProvisionProjectPools?: boolean,
        projectId?: string
        ): Promise<TaskAgent.ElasticPoolCreationResult> {

        const queryValues: any = {
            poolName: poolName,
            authorizeAllPipelines: authorizeAllPipelines,
            autoProvisionProjectPools: autoProvisionProjectPools,
            projectId: projectId
        };

        return this.beginRequest<TaskAgent.ElasticPoolCreationResult>({
            apiVersion: "6.1-preview.1",
            method: "POST",
            routeTemplate: "_apis/distributedtask/elasticpools/{poolId}",
            queryParams: queryValues,
            body: elasticPool
        });
    }

    /**
     * Returns the Elastic Pool with the specified Pool Id.
     * 
     * @param poolId - Pool Id of the associated TaskAgentPool
     */
    public async getElasticPool(
        poolId: number
        ): Promise<TaskAgent.ElasticPool> {

        return this.beginRequest<TaskAgent.ElasticPool>({
            apiVersion: "6.1-preview.1",
            routeTemplate: "_apis/distributedtask/elasticpools/{poolId}",
            routeValues: {
                poolId: poolId
            }
        });
    }

    /**
     * Get a list of all Elastic Pools.
     * 
     */
    public async getElasticPools(
        ): Promise<TaskAgent.ElasticPool[]> {

        return this.beginRequest<TaskAgent.ElasticPool[]>({
            apiVersion: "6.1-preview.1",
            routeTemplate: "_apis/distributedtask/elasticpools/{poolId}"
        });
    }

    /**
     * Update settings on a specified Elastic Pool.
     * 
     * @param elasticPoolSettings - New Elastic Pool settings data
     * @param poolId - 
     */
    public async updateElasticPool(
        elasticPoolSettings: TaskAgent.ElasticPoolSettings,
        poolId: number
        ): Promise<TaskAgent.ElasticPool> {

        return this.beginRequest<TaskAgent.ElasticPool>({
            apiVersion: "6.1-preview.1",
            method: "PATCH",
            routeTemplate: "_apis/distributedtask/elasticpools/{poolId}",
            routeValues: {
                poolId: poolId
            },
            body: elasticPoolSettings
        });
    }

    /**
     * Get a list of ElasticNodes currently in the ElasticPool
     * 
     * @param poolId - Pool id of the ElasticPool
     * @param state - Optional: Filter to only retrieve ElasticNodes in the given ElasticNodeState
     */
    public async getElasticNodes(
        poolId: number,
        state?: TaskAgent.ElasticNodeState
        ): Promise<TaskAgent.ElasticNode[]> {

        const queryValues: any = {
            '$state': state
        };

        return this.beginRequest<TaskAgent.ElasticNode[]>({
            apiVersion: "6.1-preview.1",
            routeTemplate: "_apis/distributedtask/elasticpools/{poolId}/nodes/{elasticNodeId}",
            routeValues: {
                poolId: poolId
            },
            queryParams: queryValues
        });
    }

    /**
     * Update properties on a specified ElasticNode
     * 
     * @param elasticNodeSettings - 
     * @param poolId - 
     * @param elasticNodeId - 
     */
    public async updateElasticNode(
        elasticNodeSettings: TaskAgent.ElasticNodeSettings,
        poolId: number,
        elasticNodeId: number
        ): Promise<TaskAgent.ElasticNode> {

        return this.beginRequest<TaskAgent.ElasticNode>({
            apiVersion: "6.1-preview.1",
            method: "PATCH",
            routeTemplate: "_apis/distributedtask/elasticpools/{poolId}/nodes/{elasticNodeId}",
            routeValues: {
                poolId: poolId,
                elasticNodeId: elasticNodeId
            },
            body: elasticNodeSettings
        });
    }

}

export const ElasticClientName = "Elastic.IElasticRestClient";

export function getElasticClient(pageContext: IVssPageContext, options?: IVssRestClientOptions): IElasticRestClient {
    return pageContext.getRestClient<IElasticRestClient>(ElasticClientName, options);
}

RestClients.add(ElasticClientName, { 
    factory: ElasticRestClient,
    options: {
        serviceInstanceType: "00025394-6065-48ca-87d9-7f5672854ef7"
    }
});
